/**
 * Site-wide constants used by both the app and the build-time prerenderer.
 *
 * `SITE_URL` is what canonical tags, OG tags and the sitemap are built from, so
 * it has to be the address people actually reach the site at — the custom
 * domain, never the pages.dev deploy host, which only mirrors it. Overridable
 * with `VITE_SITE_URL` for a preview build that wants to canonicalise itself.
 */
export const SITE_URL = (import.meta.env.VITE_SITE_URL ?? 'https://everydaypuzzle.app').replace(
  /\/+$/,
  '',
);

/**
 * Two forms of the name, because one string cannot do both jobs. `SITE_BRAND`
 * is how the site introduces itself — the header lockup, og:site_name, the
 * schema.org publisher. `SITE_NAME` is the short form that gets appended to
 * page titles, where the full lockup would eat the 60 characters a search
 * result actually shows.
 */
export const SITE_BRAND = 'Puzzo — Everyday Puzzles';
export const SITE_NAME = 'Puzzo';

/**
 * AdSense publisher ID, e.g. `ca-pub-0000000000000000`.
 *
 * Empty means no ad code is served anywhere and the privacy policy says the
 * site carries no advertising. One constant drives the script tag in both page
 * templates and the wording of the policy, so ads can never be live while the
 * policy still claims they are not.
 *
 * Do not fill this in until a Google-certified consent message is configured
 * in AdSense: loading the ad script without one is the compliance problem, not
 * the fix.
 */
export const ADSENSE_CLIENT: string = '';

export const APP_STORE_URL =
  'https://apps.apple.com/us/app/everyday-puzzle-logic-test/id6760844657';

export const absoluteUrl = (path: string) => `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;

export const gamePath = (id: string) => `/play/${id}`;

/**
 * Games with a past-puzzle archive. Longshot is absent because its boards come
 * from R2 rather than the Firestore daily schedule the archive pages read, so
 * linking one would be a link to a 404.
 */
export const hasArchive = (id: string) => id !== 'longshot';
export const archivePath = (id: string, date?: string) =>
  date ? `/archive/${id}/${date}` : `/archive/${id}`;
export const blogPath = (slug?: string) => (slug ? `/blog/${slug}` : '/blog');
